import {
  DocumentData,
  QueryConstraint,
  count,
  getAggregateFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  sum,
  where
} from "firebase/firestore";
import { COLLECTIONS } from "./collectionNames";
import { adminCollection, dateField, logDevelopmentError, stringField } from "./firestoreHelpers";

export type RevenueFilters = {
  startDate: string;
  endDate: string;
};

export type RevenueMoneySummary = {
  grossRevenue: number | null;
  platformFees: number | null;
  coachEarnings: number | null;
  transactionCount: number | null;
  unavailableReason: string | null;
};

export type RevenueTransactionRow = {
  id: string;
  date: Date | null;
  payer: string;
  recipient: string;
  provider: string;
  buyer: string;
  paymentType: string;
  paymentFlow: string | null;
  fundsStatus: string | null;
  grossAmount: number | null;
  amountCollected: number | null;
  platformFee: number | null;
  providerPayout: number | null;
  netEarnings: number | null;
  status: string | null;
  serviceBookingId: string | null;
  stripePaymentIntentId: string | null;
  stripeTransferId: string | null;
  releasedAt: Date | null;
  releaseFailureMessage: string | null;
};

export type RevenueAdjustmentRow = {
  id: string;
  coachUserId: string | null;
  earningId: string | null;
  bookingId: string | null;
  marketplaceTransactionId: string | null;
  refundId: string | null;
  adjustmentAmountUSD: number | null;
  remainingAmountUSD: number | null;
  status: string | null;
  createdAt: Date | null;
};

export type RevenueTrendRow = {
  label: string;
  grossRevenue: number;
  platformFees: number;
  coachEarnings: number;
  transactionCount: number;
};

export type RevenueAdminData = {
  totalGMV: number | null;
  platformRevenue: number | null;
  revenueThisMonth: number | null;
  paidCoachEarnings: number | null;
  paidPayouts: number | null;
  openRefundAdjustmentBalance: number | null;
  selectedRangeSummary: RevenueMoneySummary;
  transactions: RevenueTransactionRow[];
  refundAdjustments: RevenueAdjustmentRow[];
  trendRows: RevenueTrendRow[];
  unavailable: string[];
  lastUpdated: Date;
};

export const initialRevenueFilters: RevenueFilters = {
  startDate: "",
  endDate: ""
};

const PAID_STATUS = "paid";
const TABLE_LIMIT = 100;
const ERROR_MESSAGE_LIMIT = 180;

function numberField(data: DocumentData, key: string): number | null {
  const value = data[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function roundedMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function hasValidStoredMoneyFields(grossAmount: number | null, platformFee: number | null): boolean {
  if (grossAmount === null || platformFee === null) {
    return false;
  }

  return grossAmount >= 0 && platformFee >= 0 && platformFee <= grossAmount;
}

function netFromStoredAmounts(grossAmount: number | null, platformFee: number | null): number | null {
  if (grossAmount === null || platformFee === null || !hasValidStoredMoneyFields(grossAmount, platformFee)) {
    return null;
  }

  return roundedMoney(grossAmount - platformFee);
}

function firebaseErrorDetail(error: unknown): string {
  const record = error && typeof error === "object" ? error as { code?: unknown; message?: unknown } : {};
  const code = typeof record.code === "string" && record.code.trim().length > 0 ? record.code.trim() : null;
  const rawMessage = typeof record.message === "string" && record.message.trim().length > 0 ? record.message.trim() : null;
  const message = rawMessage
    ? rawMessage.replace(/\s+/g, " ").slice(0, ERROR_MESSAGE_LIMIT)
    : "No Firebase error message was provided.";

  return code ? `Firebase ${code}: ${message}` : message;
}

function startOfThisMonth(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

function startOfNextMonth(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 1);
}

function dateFromInput(value: string, endOfDay = false): Date | null {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return endOfDay ? new Date(year, month - 1, day + 1) : new Date(year, month - 1, day);
}

function rangeFromFilters(filters: RevenueFilters): { start: Date | null; end: Date | null } {
  return {
    start: dateFromInput(filters.startDate),
    end: dateFromInput(filters.endDate, true)
  };
}

function dateConstraints(start: Date | null, end: Date | null): QueryConstraint[] {
  const constraints: QueryConstraint[] = [];

  if (start) {
    constraints.push(where("createdAt", ">=", start));
  }

  if (end) {
    constraints.push(where("createdAt", "<", end));
  }

  return constraints;
}

function paidStatusConstraints(): QueryConstraint[] {
  return [where("status", "==", PAID_STATUS)];
}

async function safeMoneySummary(constraints: QueryConstraint[]): Promise<RevenueMoneySummary> {
  try {
    const snapshot = await getAggregateFromServer(query(adminCollection(COLLECTIONS.marketplaceTransactions), ...constraints), {
      grossRevenue: sum("grossAmountUSD"),
      platformFees: sum("platformFeeUSD"),
      transactionCount: count()
    });
    const data = snapshot.data();
    const grossRevenue = data.grossRevenue ?? 0;
    const platformFees = data.platformFees ?? 0;

    return {
      grossRevenue,
      platformFees,
      coachEarnings: roundedMoney(grossRevenue - platformFees),
      transactionCount: data.transactionCount ?? 0,
      unavailableReason: null
    };
  } catch (error) {
    logDevelopmentError(error);
    const unavailableReason = firebaseErrorDetail(error);
    return {
      grossRevenue: null,
      platformFees: null,
      coachEarnings: null,
      transactionCount: null,
      unavailableReason
    };
  }
}

async function safeRevenueForRange(start: Date, end: Date): Promise<Pick<RevenueMoneySummary, "grossRevenue" | "unavailableReason">> {
  const summary = await safeMoneySummary([...paidStatusConstraints(), ...dateConstraints(start, end)]);
  return {
    grossRevenue: summary.grossRevenue,
    unavailableReason: summary.unavailableReason
  };
}

function mapTransaction(id: string, data: DocumentData): RevenueTransactionRow {
  const grossAmount = numberField(data, "grossAmountUSD");
  const amountCollected = numberField(data, "amountCollectedUSD") ?? numberField(data, "grossAmountUSD");
  const platformFee = numberField(data, "platformFeeUSD");
  const providerPayout = numberField(data, "providerPayoutUSD") ?? numberField(data, "netAmountUSD");
  const payer = stringField(data, "payerTitle") ?? stringField(data, "payerUserId") ?? stringField(data, "customerUserId") ?? "Unavailable";
  const recipient = stringField(data, "payeeTitle") ?? stringField(data, "payeeUserId") ?? stringField(data, "providerUserId") ?? "Unavailable";
  const paymentType =
    stringField(data, "sourceType") ??
    stringField(data, "type") ??
    stringField(data, "sourceSubtitle") ??
    "Marketplace transaction";

  return {
    id,
    date: dateField(data, "createdAt") ?? dateField(data, "updatedAt"),
    payer,
    recipient,
    provider: stringField(data, "providerUserId") ?? stringField(data, "payeeUserId") ?? recipient,
    buyer: stringField(data, "buyerUserId") ?? stringField(data, "payerUserId") ?? stringField(data, "customerUserId") ?? payer,
    paymentType,
    paymentFlow: stringField(data, "paymentFlow"),
    fundsStatus: stringField(data, "fundsStatus"),
    grossAmount,
    amountCollected,
    platformFee,
    providerPayout,
    netEarnings: netFromStoredAmounts(grossAmount, platformFee),
    status: stringField(data, "status"),
    serviceBookingId: stringField(data, "serviceBookingId") ?? stringField(data, "sourceId"),
    stripePaymentIntentId: stringField(data, "stripePaymentIntentId") ?? stringField(data, "paymentIntentId"),
    stripeTransferId: stringField(data, "stripeTransferId") ?? stringField(data, "providerTransferId"),
    releasedAt: dateField(data, "releasedAt") ?? dateField(data, "transferredAt"),
    releaseFailureMessage: stringField(data, "releaseFailureMessage")
  };
}

async function fetchTransactions(constraints: QueryConstraint[]): Promise<RevenueTransactionRow[]> {
  try {
    const snapshot = await getDocs(
      query(adminCollection(COLLECTIONS.marketplaceTransactions), ...constraints, orderBy("createdAt", "desc"), limit(TABLE_LIMIT))
    );
    return snapshot.docs.map((document) => mapTransaction(document.id, document.data()));
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

async function safePaidCoachEarnings(constraints: QueryConstraint[]): Promise<number | null> {
  try {
    const snapshot = await getAggregateFromServer(
      query(adminCollection(COLLECTIONS.coachEarnings), where("status", "==", PAID_STATUS), ...constraints),
      { total: sum("net_amount_usd") }
    );
    return snapshot.data().total ?? 0;
  } catch (error) {
    logDevelopmentError(error);
    return null;
  }
}

async function safePaidPayouts(constraints: QueryConstraint[]): Promise<number | null> {
  try {
    const snapshot = await getAggregateFromServer(
      query(adminCollection(COLLECTIONS.coachPayouts), where("status", "==", PAID_STATUS), ...constraints),
      { total: sum("amount_usd") }
    );
    return snapshot.data().total ?? 0;
  } catch (error) {
    logDevelopmentError(error);
    return null;
  }
}

async function safeOpenRefundAdjustmentBalance(): Promise<number | null> {
  try {
    const snapshot = await getAggregateFromServer(
      query(adminCollection(COLLECTIONS.coachEarningAdjustments), where("status", "==", "open")),
      { total: sum("reconcileAmountRemainingUSD") }
    );
    return snapshot.data().total ?? 0;
  } catch (error) {
    logDevelopmentError(error);
    return null;
  }
}

function mapAdjustment(id: string, data: DocumentData): RevenueAdjustmentRow {
  return {
    id,
    coachUserId: stringField(data, "coachUserId"),
    earningId: stringField(data, "earningId"),
    bookingId: stringField(data, "bookingId"),
    marketplaceTransactionId: stringField(data, "marketplaceTransactionId"),
    refundId: stringField(data, "refundId"),
    adjustmentAmountUSD: numberField(data, "adjustmentAmountUSD"),
    remainingAmountUSD: numberField(data, "reconcileAmountRemainingUSD"),
    status: stringField(data, "status"),
    createdAt: dateField(data, "createdAt")
  };
}

async function fetchRefundAdjustments(): Promise<RevenueAdjustmentRow[]> {
  try {
    const snapshot = await getDocs(
      query(adminCollection(COLLECTIONS.coachEarningAdjustments), orderBy("createdAt", "desc"), limit(TABLE_LIMIT))
    );
    return snapshot.docs.map((document) => mapAdjustment(document.id, document.data()));
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

function trendRowsFromTransactions(transactions: RevenueTransactionRow[]): RevenueTrendRow[] {
  const grouped = new Map<string, RevenueTrendRow>();

  transactions.filter((transaction) => transaction.status === PAID_STATUS).forEach((transaction) => {
    const label = transaction.date ? transaction.date.toLocaleDateString() : "Unknown date";
    const current = grouped.get(label) ?? {
      label,
      grossRevenue: 0,
      platformFees: 0,
      coachEarnings: 0,
      transactionCount: 0
    };

    const grossRevenue = transaction.grossAmount ?? 0;
    const platformFees = transaction.platformFee ?? 0;
    grouped.set(label, {
      label,
      grossRevenue: roundedMoney(current.grossRevenue + grossRevenue),
      platformFees: roundedMoney(current.platformFees + platformFees),
      coachEarnings: roundedMoney(current.coachEarnings + Math.max(grossRevenue - platformFees, 0)),
      transactionCount: current.transactionCount + 1
    });
  });

  return Array.from(grouped.values()).slice(0, 14);
}

export async function fetchRevenueAdminData(filters: RevenueFilters = initialRevenueFilters): Promise<RevenueAdminData> {
  const filterRange = rangeFromFilters(filters);
  const selectedRangeConstraints = [...paidStatusConstraints(), ...dateConstraints(filterRange.start, filterRange.end)];
  const allRangeConstraints = dateConstraints(filterRange.start, filterRange.end);

  const [
    revenueThisMonthSummary,
    selectedRangeSummary,
    paidCoachEarnings,
    paidPayouts,
    openRefundAdjustmentBalance,
    transactions,
    refundAdjustments
  ] = await Promise.all([
    safeRevenueForRange(startOfThisMonth(), startOfNextMonth()),
    safeMoneySummary(selectedRangeConstraints),
    safePaidCoachEarnings(allRangeConstraints),
    safePaidPayouts(allRangeConstraints),
    safeOpenRefundAdjustmentBalance(),
    fetchTransactions(selectedRangeConstraints),
    fetchRefundAdjustments()
  ]);

  const revenueThisMonth = revenueThisMonthSummary.grossRevenue;
  const totalGMV = selectedRangeSummary.grossRevenue;
  const platformRevenue = selectedRangeSummary.platformFees;
  const unavailable: string[] = [];
  const invalidTransactions = transactions.filter((transaction) =>
    transaction.status === PAID_STATUS && !hasValidStoredMoneyFields(transaction.grossAmount, transaction.platformFee)
  );

  if (totalGMV === null) {
    unavailable.push(`Total GMV unavailable. TODO: diagnose marketplace_transactions aggregate failure. ${selectedRangeSummary.unavailableReason ?? "No Firebase error detail was provided."}`);
  }
  if (platformRevenue === null) {
    unavailable.push(`Platform Revenue unavailable. TODO: diagnose marketplace_transactions platformFeeUSD aggregate failure. ${selectedRangeSummary.unavailableReason ?? "No Firebase error detail was provided."}`);
  }
  if (revenueThisMonth === null) {
    unavailable.push(`Revenue This Month unavailable. TODO: diagnose marketplace_transactions monthly aggregate failure. ${revenueThisMonthSummary.unavailableReason ?? "No Firebase error detail was provided."}`);
  }
  if (paidCoachEarnings === null) {
    unavailable.push("Paid Coach Earnings unavailable. TODO: allow admin aggregate reads on coach_earnings.net_amount_usd for paid earnings.");
  }
  if (paidPayouts === null) {
    unavailable.push("Paid Payouts unavailable. TODO: allow admin aggregate reads on coach_payouts.amount_usd for paid payouts.");
  }
  if (openRefundAdjustmentBalance === null) {
    unavailable.push("Open refund adjustments unavailable. TODO: allow admin reads on coach_earning_adjustments.");
  }
  if (invalidTransactions.length > 0) {
    unavailable.push(`${invalidTransactions.length} paid marketplace transaction(s) have missing or invalid grossAmountUSD/platformFeeUSD values in the loaded result window. Revenue totals only use authoritative stored numeric fields.`);
  }
  unavailable.push("Disputes and Stripe processing fees are unavailable. TODO: summarize existing stripe_webhook_events/provider_payments into an admin-readable Firestore metric.");

  return {
    totalGMV,
    platformRevenue,
    revenueThisMonth,
    paidCoachEarnings,
    paidPayouts,
    openRefundAdjustmentBalance,
    selectedRangeSummary,
    transactions,
    refundAdjustments,
    trendRows: trendRowsFromTransactions(transactions),
    unavailable,
    lastUpdated: new Date()
  };
}
